import React, { useState, useEffect } from "react";
import { View, StyleSheet, Text } from "react-native";
import { ChannelList, useChatContext, InfiniteScroll } from "stream-chat-react";
import { getStreamClient } from "@/services/stream/client";
import { ChannelView } from "@/components/chat/ChannelView";
import { ChannelPreviewCustom } from "@/components/chat/ChannelPreviewCustom.web";
import type { UniverseStreamChannel } from "@/services/stream/types";
import { navigationEvents, NAVIGATION_EVENT_TYPES } from "@/utils/navigationEvents";
import { ChannelListHeader } from "@/components/chat/ChannelListHeader";
import { useLocalSearchParams } from "expo-router";
import type { FilterOption } from "@/components/chat/ChannelFilterDropdown";

const STATUS_FILTER_MAP: Partial<Record<FilterOption, string>> = {
    'Active': 'active',
    // The backend stores WaConversations::IN_CONVERSATION as 'in conversation'
    // (with a space) in the channel's custom_data.status.
    'In Conversation': 'in conversation',
    'Pending': 'pending',
    'Inactive': 'inactive',
};

const CustomInfiniteScrollPaginator = (props: any) => {
    const { children, loadNextPage, hasNextPage, isLoading } = props;
    return (
        <InfiniteScroll
            loadNextPage={loadNextPage}
            hasNextPage={hasNextPage}
            isLoading={isLoading}
        >
            {children}
        </InfiniteScroll>
    );
};

export default function InboxScreen() {
    const client = getStreamClient();
    const userId = client.userID;
    const { channel: selectedChannel, setActiveChannel } = useChatContext();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedFilter, setSelectedFilter] = useState<FilterOption>('All');
    const { channelId: urlChannelId } = useLocalSearchParams<{ channelId: string }>();

    useEffect(() => {
        if (!urlChannelId || (selectedChannel && selectedChannel.id === urlChannelId)) {
            return;
        }

        let isMounted = true;
        const selectChannelFromUrl = async () => {
            try {
                const filters = { id: urlChannelId };
                const channels = await client.queryChannels(filters);
                if (isMounted && channels.length > 0) {
                    setActiveChannel(channels[0] as UniverseStreamChannel);
                }
            } catch (error) {
                console.error("Error selecting channel from URL:", error);
            }
        };
        selectChannelFromUrl();

        return () => {
            isMounted = false;
        };
    }, [urlChannelId, client, setActiveChannel, selectedChannel?.id]);

    useEffect(() => {
        const subscription = navigationEvents.addListener(
            NAVIGATION_EVENT_TYPES.SELECT_CHANNEL,
            async ({ channelId }: { channelId: string }) => {
                if (selectedChannel && selectedChannel.id === channelId) {
                    return;
                }
                try {
                    const filters = { id: channelId };
                    const channels = await client.queryChannels(filters);
                    if (channels.length > 0) {
                        setActiveChannel(channels[0] as UniverseStreamChannel);
                    }
                } catch (error) {
                    console.error("Error selecting channel from event:", error);
                }
            }
        );

        return () => {
            subscription.remove();
        };
    }, [client, setActiveChannel, selectedChannel?.id]);

    // Expose the currently-open channel so the NotificationProvider (which sits outside
    // the Chat context) can avoid notifying for the chat the user is actively viewing.
    useEffect(() => {
        (global as any).__CURRENT_CHANNEL_ID__ = selectedChannel?.id ?? null;
        return () => {
            (global as any).__CURRENT_CHANNEL_ID__ = null;
        };
    }, [selectedChannel?.id]);

    if (!userId) return null;

    const statusValue = STATUS_FILTER_MAP[selectedFilter];
    const filters = {
        type: "messaging",
        members: { $in: [userId] },
        ...(searchQuery ? { name: { $autocomplete: searchQuery } } : {}),
    };

    const channelRenderFilterFn = statusValue
        ? (channels: any[]) => channels.filter((ch) => ch.data?.custom_data?.status === statusValue)
        : undefined;

    return (
        <View style={styles.tabletContainer}>
            <View style={styles.listContainer}>
                <ChannelListHeader onSearch={setSearchQuery} onFilterChange={setSelectedFilter} />
                <ChannelList
                    filters={filters}
                    sort={{ last_message_at: -1 }}
                    Paginator={CustomInfiniteScrollPaginator}
                    Preview={ChannelPreviewCustom}
                    channelRenderFilterFn={channelRenderFilterFn}
                />
            </View>
            <View style={styles.chatContainer}>
                {selectedChannel ? (
                    <ChannelView channel={selectedChannel as UniverseStreamChannel} isTablet={true} />
                ) : (
                    <View style={styles.noChannelSelected}>
                        <Text style={styles.noChannelText}>Select a chat to start messaging</Text>
                    </View>
                )}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    tabletContainer: {
        flex: 1,
        flexDirection: 'row',
        backgroundColor: 'white',
    },
    listContainer: {
        width: 350,
        borderRightWidth: StyleSheet.hairlineWidth,
        borderRightColor: '#E5E5E5',
    },
    chatContainer: {
        flex: 1,
        backgroundColor: '#f8fbff',
    },
    noChannelSelected: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    noChannelText: {
        fontSize: 18,
        color: '#9CA3AF',
    },
});
